import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import {
  X, ChevronDown, Grid, LayoutGrid, Search,
  SlidersHorizontal
} from 'lucide-react';
import { artworks, categories, collections, curatedViews, filterOptions } from '../data/artworksData';

const GalleryPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ru' ? 'ru' : 'en';
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'masonry'
  const [displayCount, setDisplayCount] = useState(9);

  // Filters state
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || 'all',
    series: searchParams.get('series') || 'all',
    status: 'all',
    priceRange: 'all',
    year: 'all',
    mood: 'all',
    dominantColor: 'all',
    curatedView: searchParams.get('curated') || 'all'
  });

  // Infinite scroll
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false
  });

  // Filter artworks (defined before useEffects that depend on it)
  const filteredArtworks = useMemo(() => {
    let result = [...artworks];

    // Apply curated view filter
    if (filters.curatedView !== 'all') {
      const curatedView = curatedViews.find(cv => cv.id === filters.curatedView);
      if (curatedView) {
        result = result.filter(curatedView.filter);
      }
    }

    // Category filter
    if (filters.category !== 'all') {
      result = result.filter(a => a.category === filters.category);
    }

    // Series filter
    if (filters.series !== 'all') {
      result = result.filter(a => a.series === filters.series);
    }

    // Status filter
    if (filters.status !== 'all') {
      result = result.filter(a => a.status === filters.status);
    }

    // Price range filter
    if (filters.priceRange !== 'all') {
      result = result.filter(a => a.priceRange === filters.priceRange);
    }

    // Year filter
    if (filters.year !== 'all') {
      result = result.filter(a => a.year.toString() === filters.year);
    }

    // Mood filter
    if (filters.mood !== 'all') {
      result = result.filter(a => a.mood === filters.mood);
    }

    // Color filter
    if (filters.dominantColor !== 'all') {
      result = result.filter(a => a.dominantColor === filters.dominantColor);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(a =>
        a.title.ru.toLowerCase().includes(query) ||
        a.title.en.toLowerCase().includes(query) ||
        a.technique.ru.toLowerCase().includes(query) ||
        a.technique.en.toLowerCase().includes(query)
      );
    }

    return result;
  }, [filters, searchQuery]);

  // Load more when scrolling
  useEffect(() => {
    if (inView && displayCount < filteredArtworks.length) {
      setDisplayCount(prev => Math.min(prev + 6, filteredArtworks.length));
    }
  }, [inView, displayCount, filteredArtworks.length]);

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(9);
  }, [filters, searchQuery]);

  // Displayed artworks (for infinite scroll)
  const displayedArtworks = filteredArtworks.slice(0, displayCount);

  // Handle filter change
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));

    // Update URL params for main filters
    if (key === 'category' || key === 'series' || key === 'curatedView') {
      const newParams = new URLSearchParams(searchParams);
      if (value === 'all') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams]);

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      category: 'all',
      series: 'all',
      status: 'all',
      priceRange: 'all',
      year: 'all',
      mood: 'all',
      dominantColor: 'all',
      curatedView: 'all'
    });
    setSearchQuery('');
    setSearchParams({});
  };

  // Check if any filter is active
  const hasActiveFilters = Object.values(filters).some(v => v !== 'all') || searchQuery.trim();

  return (
    <div className="min-h-screen bg-gallery-bg pt-16 md:pt-20">
      {/* Header - Mobile-first */}
      <div className="bg-white border-b border-gallery-border">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 md:py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-gallery-text mb-3 sm:mb-4">
              {lang === 'ru' ? 'Галерея работ' : 'Art Gallery'}
            </h1>
            <p className="text-gallery-muted max-w-xl mx-auto text-sm sm:text-base px-2">
              {lang === 'ru'
                ? 'Коллекция оригинальных произведений искусства'
                : 'Collection of original artworks'
              }
            </p>
          </motion.div>
        </div>
      </div>

      {/* Curated Views Tabs - Touch-friendly horizontal scroll */}
      <div className="bg-white border-b border-gallery-border sticky top-16 md:top-20 z-30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 py-3 sm:py-4 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            <button
              onClick={() => handleFilterChange('curatedView', 'all')}
              className={`min-h-[44px] px-4 py-2.5 text-sm tracking-wide whitespace-nowrap transition-colors flex-shrink-0 ${
                filters.curatedView === 'all'
                  ? 'bg-gallery-text text-white'
                  : 'text-gallery-muted hover:text-gallery-text active:text-gallery-text'
              }`}
            >
              {lang === 'ru' ? 'Все работы' : 'All Works'}
            </button>
            {curatedViews.map(view => (
              <button
                key={view.id}
                onClick={() => handleFilterChange('curatedView', view.id)}
                className={`min-h-[44px] px-4 py-2.5 text-sm tracking-wide whitespace-nowrap transition-colors flex-shrink-0 ${
                  filters.curatedView === view.id
                    ? 'bg-gallery-text text-white'
                    : 'text-gallery-muted hover:text-gallery-text active:text-gallery-text'
                }`}
              >
                {view.title[lang]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filter Bar - Mobile-first responsive */}
      <div className="bg-gallery-bg border-b border-gallery-border">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-between gap-3 sm:gap-4">
            {/* Search - Full width on mobile */}
            <div className="relative flex-1 w-full sm:max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gallery-muted" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={lang === 'ru' ? 'Поиск работ...' : 'Search artworks...'}
                className="w-full pl-12 pr-4 py-3 min-h-[44px] bg-white border border-gallery-border text-gallery-text placeholder-gallery-muted focus:outline-none focus:border-gallery-accent transition-colors text-base"
              />
            </div>

            {/* Filter Controls - Row on mobile */}
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
              {/* Category Dropdown - Touch-friendly */}
              <div className="relative flex-1 sm:flex-none">
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="appearance-none w-full sm:w-auto min-h-[44px] px-4 py-2.5 pr-10 bg-white border border-gallery-border text-gallery-text text-sm focus:outline-none focus:border-gallery-accent cursor-pointer"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.title[lang]}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gallery-muted pointer-events-none" size={16} />
              </div>

              {/* More Filters Button - Touch-friendly */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center gap-2 min-h-[44px] min-w-[44px] px-3 sm:px-4 py-2.5 border transition-colors ${
                  showFilters
                    ? 'bg-gallery-text text-white border-gallery-text'
                    : 'bg-white border-gallery-border text-gallery-text hover:border-gallery-accent active:border-gallery-accent'
                }`}
                aria-label={lang === 'ru' ? 'Фильтры' : 'Filters'}
                aria-expanded={showFilters}
              >
                <SlidersHorizontal size={18} />
                <span className="text-sm hidden sm:inline">
                  {lang === 'ru' ? 'Фильтры' : 'Filters'}
                </span>
                {hasActiveFilters && (
                  <span className="w-2 h-2 bg-gallery-accent rounded-full" />
                )}
              </button>

              {/* View Mode Toggle - Hidden on mobile, touch-friendly */}
              <div className="hidden sm:flex items-center border border-gallery-border bg-white">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors ${
                    viewMode === 'grid' ? 'bg-gallery-text text-white' : 'text-gallery-muted hover:text-gallery-text'
                  }`}
                  aria-label="Grid view"
                >
                  <Grid size={18} />
                </button>
                <button
                  onClick={() => setViewMode('masonry')}
                  className={`min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors ${
                    viewMode === 'masonry' ? 'bg-gallery-text text-white' : 'text-gallery-muted hover:text-gallery-text'
                  }`}
                  aria-label="Masonry view"
                >
                  <LayoutGrid size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Extended Filters Panel - Mobile-first */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white border-b border-gallery-border overflow-hidden"
          >
            <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
                {/* Status */}
                <div>
                  <label className="block text-xs text-gallery-muted uppercase tracking-wider mb-1.5 sm:mb-2">
                    {lang === 'ru' ? 'Статус' : 'Status'}
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2.5 border border-gallery-border text-sm focus:outline-none focus:border-gallery-accent"
                  >
                    {filterOptions.status.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.title[lang]}</option>
                    ))}
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-xs text-gallery-muted uppercase tracking-wider mb-1.5 sm:mb-2">
                    {lang === 'ru' ? 'Цена' : 'Price'}
                  </label>
                  <select
                    value={filters.priceRange}
                    onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2.5 border border-gallery-border text-sm focus:outline-none focus:border-gallery-accent"
                  >
                    {filterOptions.priceRange.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.title[lang]}</option>
                    ))}
                  </select>
                </div>

                {/* Year */}
                <div>
                  <label className="block text-xs text-gallery-muted uppercase tracking-wider mb-1.5 sm:mb-2">
                    {lang === 'ru' ? 'Год' : 'Year'}
                  </label>
                  <select
                    value={filters.year}
                    onChange={(e) => handleFilterChange('year', e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2.5 border border-gallery-border text-sm focus:outline-none focus:border-gallery-accent"
                  >
                    {filterOptions.year.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.title[lang]}</option>
                    ))}
                  </select>
                </div>

                {/* Series */}
                <div>
                  <label className="block text-xs text-gallery-muted uppercase tracking-wider mb-1.5 sm:mb-2">
                    {lang === 'ru' ? 'Серия' : 'Series'}
                  </label>
                  <select
                    value={filters.series}
                    onChange={(e) => handleFilterChange('series', e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2.5 border border-gallery-border text-sm focus:outline-none focus:border-gallery-accent"
                  >
                    <option value="all">{lang === 'ru' ? 'Все серии' : 'All Series'}</option>
                    {collections.map(col => (
                      <option key={col.id} value={col.id}>{col.title[lang]}</option>
                    ))}
                  </select>
                </div>

                {/* Mood */}
                <div>
                  <label className="block text-xs text-gallery-muted uppercase tracking-wider mb-1.5 sm:mb-2">
                    {lang === 'ru' ? 'Настроение' : 'Mood'}
                  </label>
                  <select
                    value={filters.mood}
                    onChange={(e) => handleFilterChange('mood', e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2.5 border border-gallery-border text-sm focus:outline-none focus:border-gallery-accent"
                  >
                    {filterOptions.mood.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.title[lang]}</option>
                    ))}
                  </select>
                </div>

                {/* Dominant Color */}
                <div>
                  <label className="block text-xs text-gallery-muted uppercase tracking-wider mb-1.5 sm:mb-2">
                    {lang === 'ru' ? 'Цвет' : 'Color'}
                  </label>
                  <select
                    value={filters.dominantColor}
                    onChange={(e) => handleFilterChange('dominantColor', e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2.5 border border-gallery-border text-sm focus:outline-none focus:border-gallery-accent"
                  >
                    {filterOptions.dominantColor.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.title[lang]}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Clear Filters - Touch-friendly */}
              {hasActiveFilters && (
                <div className="mt-4 pt-4 border-t border-gallery-border">
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-2 min-h-[44px] text-gallery-muted hover:text-gallery-accent active:text-gallery-accent transition-colors text-sm px-2 -ml-2"
                  >
                    <X size={16} />
                    {lang === 'ru' ? 'Сбросить все фильтры' : 'Clear all filters'}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results Count */}
      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <p className="text-gallery-muted text-xs sm:text-sm">
          {lang === 'ru'
            ? `Показано ${displayedArtworks.length} из ${filteredArtworks.length} работ`
            : `Showing ${displayedArtworks.length} of ${filteredArtworks.length} works`
          }
        </p>
      </div>

      {/* Gallery Grid - Mobile-first responsive */}
      <div className="container mx-auto px-4 sm:px-6 pb-16 sm:pb-20 md:pb-24">
        {filteredArtworks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 sm:py-20 md:py-24"
          >
            <p className="text-gallery-muted text-base sm:text-lg mb-4">
              {lang === 'ru' ? 'Работы не найдены' : 'No artworks found'}
            </p>
            <button
              onClick={clearFilters}
              className="text-gallery-accent hover:underline active:underline min-h-[44px] px-4 py-2"
            >
              {lang === 'ru' ? 'Сбросить фильтры' : 'Clear filters'}
            </button>
          </motion.div>
        ) : viewMode === 'masonry' ? (
          // Masonry Layout - Responsive columns
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-6 lg:gap-8 space-y-4 sm:space-y-6 lg:space-y-8">
            {displayedArtworks.map((artwork, idx) => (
              <ArtworkCard key={artwork.id} artwork={artwork} lang={lang} index={idx} />
            ))}
          </div>
        ) : (
          // Grid Layout - Mobile-first: 1 col → 2 col → 3 col
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10 xl:gap-12">
            {displayedArtworks.map((artwork, idx) => (
              <ArtworkCard key={artwork.id} artwork={artwork} lang={lang} index={idx} />
            ))}
          </div>
        )}

        {/* Load More Trigger */}
        {displayCount < filteredArtworks.length && (
          <div ref={loadMoreRef} className="py-8 sm:py-12 text-center">
            <div className="inline-block w-8 h-8 border-2 border-gallery-accent border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

// Artwork Card Component - Mobile-first responsive
const ArtworkCard = ({ artwork, lang, index }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: (index % 3) * 0.1 }}
      className="break-inside-avoid"
    >
      <Link to={`/artwork/${artwork.id}`} className="group block">
        <div className="relative overflow-hidden bg-gallery-light mb-3 sm:mb-4">
          <div className="aspect-[4/5]">
            <img
              src={artwork.image}
              alt={artwork.title[lang]}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
          </div>

          {/* Overlay on hover/active */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 group-active:bg-black/20 transition-colors duration-300" />

          {/* Status Badge - Responsive positioning */}
          {artwork.status !== 'available' && (
            <div className="absolute top-2 right-2 sm:top-4 sm:right-4">
              <span className={`px-2 sm:px-3 py-1 text-xs tracking-wider uppercase ${
                artwork.status === 'sold'
                  ? 'bg-gallery-text text-white'
                  : 'bg-gallery-accent text-white'
              }`}>
                {artwork.status === 'sold'
                  ? (lang === 'ru' ? 'Продано' : 'Sold')
                  : (lang === 'ru' ? 'Резерв' : 'Reserved')
                }
              </span>
            </div>
          )}

          {/* Quick View Overlay - Hidden on mobile, shown on hover for desktop */}
          <div className="absolute inset-0 hidden sm:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="px-4 sm:px-6 py-2.5 sm:py-3 bg-white text-gallery-text text-sm tracking-wider uppercase">
              {lang === 'ru' ? 'Подробнее' : 'View Details'}
            </span>
          </div>
        </div>

        {/* Info - Responsive typography */}
        <div className="text-center px-1">
          <h3 className="font-serif text-base sm:text-lg text-gallery-text mb-0.5 sm:mb-1 group-hover:text-gallery-accent transition-colors line-clamp-2">
            {artwork.title[lang]}
          </h3>
          <p className="text-gallery-muted text-xs sm:text-sm">
            {artwork.technique[lang]}
          </p>
          <p className="text-gallery-muted text-xs sm:text-sm">
            {artwork.size} &bull; {artwork.year}
          </p>
          {artwork.status === 'available' && artwork.price && (
            <p className="text-gallery-text font-medium mt-1.5 sm:mt-2 text-sm sm:text-base">
              EUR {artwork.price.toLocaleString()}
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default GalleryPage;
